import type { JSX } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { WireScheduledPrompt } from '@logistics/contracts';
import { failureOf } from '@/lib/result';
import { cn } from '@/lib/utils';
import { api } from '../client';
import { button, formError } from '../shell/look';
import { Modal } from '../shell/modal';
import { useToast } from '../shell/toast';
import { firstScheduleError, scheduleFormSchema, toDraft } from './schedule-form';
import type { ScheduleForm, ScheduleValues } from './schedule-form';
import { WhatFields, WhenFields } from './schedule-fields';
import { WhoFields } from './schedule-recipients';
import { SCHEDULES_KEY } from './use-schedules';

export type EditorForm = UseFormReturn<ScheduleForm, unknown, ScheduleValues>;

/**
 * New or edited schedule. The prompt arrives prefilled from a chat message
 * that answered well; what is left is when it runs and who receives it.
 */
export function ScheduleEditor({
  editing,
  initial,
  timezones,
  onClose,
}: {
  editing?: WireScheduledPrompt | undefined;
  initial: ScheduleForm;
  timezones: readonly string[];
  onClose: () => void;
}): JSX.Element {
  const form: EditorForm = useForm<ScheduleForm, unknown, ScheduleValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: initial,
  });
  const [refused, setRefused] = useState<string | undefined>(undefined);
  const toast = useToast();
  const client = useQueryClient();

  const submit = form.handleSubmit(async (values) => {
    setRefused(undefined);
    const body = toDraft(values);
    const r =
      editing === undefined
        ? await api.schedules.add({ body })
        : await api.schedules.update({ params: { id: editing.id }, body });
    if (r.status !== 201 && r.status !== 200) {
      const problems = (r.body as { problems?: string[] } | null)?.problems;
      return setRefused(
        problems?.join('; ') ?? failureOf(r).detail ?? `The API answered ${r.status}.`,
      );
    }
    toast(editing === undefined ? `${values.name} scheduled` : `${values.name} saved`);
    await client.invalidateQueries({ queryKey: SCHEDULES_KEY });
    onClose();
  });
  const error = firstScheduleError(form.formState.errors) ?? refused;

  return (
    <Modal
      eyebrow="SCHEDULED PROMPTS"
      title={editing === undefined ? 'Schedule a prompt' : `Edit ${editing.name}`}
      width={640}
      tall
      onClose={onClose}
    >
      <form
        noValidate
        className="flex flex-col gap-4 overflow-y-auto px-7 py-6"
        onSubmit={(event) => void submit(event)}
      >
        <WhatFields form={form} />
        <WhenFields form={form} timezones={timezones} />
        <WhoFields form={form} />
        {error !== undefined && (
          <div role="alert" className={formError}>
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2.5 pt-1.5">
          <button type="button" onClick={onClose} className={button.outline}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={cn(button.primary, 'px-[18px]')}
          >
            {editing === undefined ? 'Schedule' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
